import { useEffect, useState } from "react";

// ── Lab hero stats (public, cross-origin) ──────────────────────────────────
// GET https://api.atxfilmlab.com/v1/stats → { data: { customers, drop_offs,
// rolls, as_of } }. Public, ACAO:*, 60s cache.

export type LabStats = {
  rolls: number;
  drop_offs: number;
  customers: number;
  as_of?: string;
};

// Seed targets (last-known values from the legacy site) so the count-up has
// something to animate to before the live fetch resolves.
export const LAB_STATS_SEED: LabStats = {
  rolls: 21919,
  drop_offs: 9638,
  customers: 3258,
};

export function useLabStats(enabled = true): LabStats {
  const [stats, setStats] = useState<LabStats>(LAB_STATS_SEED);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    fetch("https://api.atxfilmlab.com/v1/stats")
      .then((r) => r.json())
      .then((j) => {
        const d = j?.data;
        if (!alive || !d) return;
        setStats((prev) => ({
          rolls: typeof d.rolls === "number" ? d.rolls : prev.rolls,
          drop_offs: typeof d.drop_offs === "number" ? d.drop_offs : prev.drop_offs,
          customers: typeof d.customers === "number" ? d.customers : prev.customers,
          as_of: d.as_of,
        }));
      })
      .catch(() => {
        /* keep seeded values */
      });
    return () => {
      alive = false;
    };
  }, [enabled]);
  return stats;
}

// ── Letterboxd stats (same-origin /apis/stats via LETTERBOXD binding) ───────

export type LbStats = {
  films_watched: number;
  reviews: number;
  avg_rating: number | null;
  films_this_year: number;
  lists: number;
};

export function useLbStats(): { data: LbStats | null; error: boolean } {
  const [data, setData] = useState<LbStats | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let alive = true;
    fetch("/apis/stats")
      .then((r) => r.json())
      .then((j) => {
        if (!alive) return;
        if (j?.data) setData(j.data);
        else setError(true);
      })
      .catch(() => alive && setError(true));
    return () => {
      alive = false;
    };
  }, []);
  return { data, error };
}

// ── Letterboxd reviews (same-origin /apis/reviews?include=tmdb) ─────────────

export type LbTmdb = {
  poster_url?: string;
  enriched?: boolean;
  runtime?: number;
  genres?: string[];
};

export type LbReview = {
  film: { slug: string; name: string; year?: number | string; uri?: string };
  rating: number | null;
  watched_date: string;
  rewatch?: boolean;
  review_html?: string;
  tmdb?: LbTmdb;
};

export type ReviewsState = {
  reviews: LbReview[] | null;
  loading: boolean;
  error: boolean;
};

export function useLbReviews(perPage = 10): ReviewsState {
  const [state, setState] = useState<ReviewsState>({
    reviews: null,
    loading: true,
    error: false,
  });
  useEffect(() => {
    let alive = true;
    fetch(`/apis/reviews?per_page=${perPage}&include=tmdb`)
      .then((r) => r.json())
      .then((j) => {
        if (!alive) return;
        setState({ reviews: Array.isArray(j?.data) ? j.data : [], loading: false, error: false });
      })
      .catch(() => {
        if (alive) setState({ reviews: null, loading: false, error: true });
      });
    return () => {
      alive = false;
    };
  }, [perPage]);
  return state;
}
